import { DayDate } from "@cmp-src/data-display/dates/DayDate";
import { ResourceLocation } from "@cmp-src/data-display/resource/ResourceLocation";
import { EditIcon } from "@cmp-src/icons/TacticIcons/EditIcon";
import { ViewDeskIcon } from "@cmp-src/icons/TacticIcons/VIewDeskIcon";
import { ISchedule, ISlot, WorkplaceRequestElement } from "@gettactic/api";
import { Button } from "@v2/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@v2/components/ui/dropdown-menu";

import { useAreas, useAuthenticated } from "@gettactic/hooks";
import { useRouter } from "next/router";
import {
  isRoom,
  createMapLink,
  cn,
  findCheckinTargetSlot,
  useBooleanState,
  useDisclosure
} from "@gettactic/helpers";
import { useReserveModal } from "@/hooks/useReserveModal";
import { RoomData } from "./RoomData";
import { AnyWorkplaceData } from "./AnyWorkplaceData";
import { RoomActions } from "./RoomActions";
import { MapWithMiniOfficePhoto } from "./MapWithMiniOfficePhoto";
import { UsersInOffice } from "./UsersInOffice";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/api";
import { ReactNode, useCallback } from "react";
import { getReservationDataFromSlot } from "@gettactic/helpers/src/reservation/getReservationDataFromSlot";
import { ReservationModals } from "@webV2/components/reservation-modals/ReservationModals";
import { getResourceCoverUrl } from "@gettactic/helpers/src/resources/getResourceCoverUrl";
import useResource from "@/hooks/useResource";
import { JoinButton } from "@cmp-src/components/JoinButton/JoinButton";
import { ConfirmModal } from "@cmp-src/overlay/modals/ConfirmModal";
import { dateStringToLocalTime } from "@gettactic/helpers/src/date-time/dateStringToLocalTime";
import { useWorkplaceRequestsByReservation } from "@/hooks/useWorkplaceRequestsByReservation";
import { useWorkplaceRequestModal } from "../../../contexts/WorkplaceRequestModalContext";
import { useState } from "react";
import { RequestDetailsPage } from "@webV2/pages/settings/WorkplaceRequestsPage/components/RequestDetailsPage";
import { canScheduleOthers } from "@gettactic/helpers";
import { useIsMobile } from "@v2/hooks/use-mobile";

export type DashboardEventDetailsProps = {
  schedule: ISchedule;
  calendarSwitch?: ReactNode;
  onCancel?: () => void;
  teamSchedule?: ReactNode;
  isOrgPrivacyEnabled: boolean;
};

export const DashboardEventDetails = ({
  schedule: incomingSchedule,
  calendarSwitch,
  teamSchedule,

  onCancel,
  isOrgPrivacyEnabled
}: DashboardEventDetailsProps) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { openModal, isOpen: contextIsOpen } = useWorkplaceRequestModal();
  const [localSelectedRequest, setLocalSelectedRequest] =
    useState<WorkplaceRequestElement | null>(null);

  const isMobile = useIsMobile();

  const isLoading = useBooleanState();
  const checkOutModalDisclosure = useDisclosure();

  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { openEditModal, openCancelModal } = useReserveModal();

  const { data: scheduleUpdated } = useQuery({
    queryKey: ["schedule", incomingSchedule.id],
    queryFn: () => api.client.schedules.scheduleById(incomingSchedule.id),
    enabled: !!incomingSchedule.id
  });

  const schedule = scheduleUpdated?.result || incomingSchedule;

  const { resource } = useResource({ id: schedule?.resource.id });
  const officeId = schedule.resource.office_id;
  const parentId = schedule.resource.parent_id;
  const { areas: areaList } = useAreas(officeId);
  const thisOffice = authenticatedUser.offices?.byId[officeId];
  const officeName = thisOffice?.name;
  const areaName = parentId ? areaList?.byId[parentId]?.name : undefined;

  const targetSlot = schedule
    ? findCheckinTargetSlot(schedule, authenticatedUser.user?.id ?? "")
    : undefined;

  const { allowCheckIn, allowCheckOut } = getReservationDataFromSlot(
    targetSlot as unknown as ISlot
  );

  const link = createMapLink({
    date: dateStringToLocalTime(schedule.start),
    officeId,
    resourceId: schedule.resource?.id,
    floorId: schedule.resource?.parent_id ?? undefined,
    startTime: dateStringToLocalTime(schedule.start),
    endTime: dateStringToLocalTime(schedule.end)
  });

  if (!schedule) {
    return null;
  }

  const { allElements: existingRequests, isFetching: isLoadingExisting } =
    useWorkplaceRequestsByReservation(schedule.id, {
      enabled: !!schedule.id
    });

  const renderAsRoom = isRoom(schedule.resource.type);

  const resourceName = schedule.resource.name;
  const meetingName = schedule.title;
  const eventTitle = renderAsRoom
    ? (meetingName ?? resourceName)
    : resourceName;

  const userSlot = schedule.slots.find(
    (slot) => slot.user.id === authenticatedUser.user?.id
  ) as ISlot;

  const { canEdit, canRespondCancel } = getReservationDataFromSlot(userSlot);

  // Function to handle cancel action
  const handleCancel = useCallback(async () => {
    if (!schedule) return;
    await openCancelModal([schedule]);
  }, [schedule, openCancelModal]);

  const handleEdit = useCallback(() => {
    if (!schedule) return;

    openEditModal(schedule, true);
  }, [schedule, openEditModal]);
  const resourceMediaUrl = resource ? getResourceCoverUrl(resource) : undefined;

  const handleCheckInClick = () => {
    /*if (!onCheckin) return;
    const slot = reservation.slots[0];
    onCheckin(slot.resource.id, reservation.id, slot.id);*/
  };

  const handleCheckOutClick = () => {
    /*if (!onCheckout) return;
    const slot = reservation.slots[0];
    onCheckout(slot.resource.id, reservation.id, slot.id);
    setIsCheckOutModalOpen(false);*/
  };

  return (
    <div className="mb-4 flex w-full flex-col items-center justify-center md:mt-16 md:mb-16">
      <div className="flex h-full w-full flex-col">
        <div className="relative hidden flex-row items-center justify-between pb-4 md:flex">
          <div>
            <DayDate
              apiDate={schedule.start}
              tz={schedule.time_zone}
              className="v2-header-1"
            />
          </div>
          <div className="flex gap-2">
            <JoinButton
              iconOnly={true}
              onlineMeeting={schedule.online_meeting ?? null}

              // those are comming from the logged in user agenda,
              // so no need to doublecheck
              hasAccess={true}

              onRefetch={() => {
                queryClient.invalidateQueries(["schedule", schedule.id]);
                queryClient.invalidateQueries(["schedules"]);
              }}
              wrapper={(children, icon, props) => (
                <Button
                  variant="outlinePale"
                  {...props}
                  className={cn("flex items-center px-3", props.className)}
                >
                  {icon}
                  {children}
                </Button>
              )}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outlinePale"
                  className="px-3"
                  disabled={!canEdit && !canRespondCancel}
                >
                  <EditIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEdit && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit();
                    }}
                  >
                    Manage
                  </DropdownMenuItem>
                )}
                {canRespondCancel && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancel();
                    }}
                    variant="danger"
                  >
                    Remove
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              onClick={() => {
                router.push(link);
              }}
            >
              <ViewDeskIcon />
              Locate
            </Button>
          </div>
        </div>
        {schedule.start && (
          <MapWithMiniOfficePhoto
            resourceId={schedule.resource.id}
            officeId={schedule.resource.office_id}
            parentId={schedule.resource.parent_id ?? ""}
            startDate={schedule.start.split("T")[0]}
            resourceImage={resourceMediaUrl}
          />
        )}
        <div className="flex flex-row items-center justify-between px-2.5 pt-4 md:px-0 md:pt-[30px]">
          <div className="flex flex-col justify-between">
            <div className="font-bold text-lg">{eventTitle}</div>
            <ResourceLocation
              location={officeName || "Location"}
              floor={areaName || "Floor"}
            />
          </div>
          {renderAsRoom && (
            <RoomActions
              schedule={schedule}
              userSlot={userSlot}
              remoteEnabled={true}
            />
          )}
        </div>

        {renderAsRoom && (
          <div className="px-0 py-6 md:py-0">
            <RoomData
              schedule={schedule}
              resource={resource}
              workplaceRequests={existingRequests}
            />
          </div>
        )}
        <div className="px-2.5 md:px-0 md:pt-[48px]">
          {teamSchedule ? (
            <div className="flex flex-col gap-4">
              {renderAsRoom ? (
                isOrgPrivacyEnabled ? null : (
                  <UsersInOffice officeId={officeId} isRoom={renderAsRoom} />
                )
              ) : (
                <AnyWorkplaceData
                  schedule={schedule}
                  officeId={officeId}
                  workplaceRequests={existingRequests}
                  teamSchedule={teamSchedule}
                  onRequestClick={
                    contextIsOpen
                      ? openModal
                      : (request) => setLocalSelectedRequest(request)
                  }
                  isOrgPrivacyEnabled={isOrgPrivacyEnabled}
                />
              )}
            </div>
          ) : renderAsRoom ? (
            isOrgPrivacyEnabled ? null : (
              <UsersInOffice officeId={officeId} isRoom={renderAsRoom} />
            )
          ) : (
            <AnyWorkplaceData
              schedule={schedule}
              officeId={officeId}
              workplaceRequests={existingRequests}
              onRequestClick={
                contextIsOpen
                  ? openModal
                  : (request) => setLocalSelectedRequest(request)
              }
              isOrgPrivacyEnabled={isOrgPrivacyEnabled}
            />
          )}
        </div>
        <div className={"flex gap-2.5 px-2.5 md:hidden"}>
          <div className={cn("w-full", isMobile && "invisible mb-12")}>
            <Button
              className={"w-full"}
              variant={"secondary"}
              onClick={onCancel}
            >
              Cancel
            </Button>
          </div>
          {(allowCheckIn || allowCheckOut) && (
            <div className={"w-full"}>
              {allowCheckIn && (
                <Button
                  className={"w-full"}
                  onClick={handleCheckInClick}
                  disabled={isLoading.state}
                >
                  {isLoading.state ? "Processing..." : "Check In"}
                </Button>
              )}
              {allowCheckOut && (
                <Button
                  className={"w-full"}
                  onClick={() => checkOutModalDisclosure.open()}
                  disabled={isLoading.state}
                  variant="secondary"
                >
                  {isLoading.state ? "Processing..." : "Check Out"}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      <ReservationModals />
      {checkOutModalDisclosure.isOpen && (
        <ConfirmModal
          title="Check out"
          message="Are you sure you want to check out?"
          onConfirm={handleCheckOutClick}
          onOpenChange={checkOutModalDisclosure.onOpenChange}
          onCancel={checkOutModalDisclosure.close}
          isOpen={checkOutModalDisclosure.isOpen}
        />
      )}
      {localSelectedRequest && (
        <RequestDetailsPage
          dontTouchUrl={true}
          requestId={localSelectedRequest.id}
          isAdmin={canScheduleOthers(authenticatedUser)}
          onRequestUpdate={(updatedRequest: WorkplaceRequestElement) => {
            setLocalSelectedRequest(updatedRequest);
          }}
          isPageMode={false}
          initialRequest={localSelectedRequest}
          onClose={() => setLocalSelectedRequest(null)}
          schedule={schedule}
        />
      )}
    </div>
  );
};
